import { memo, useCallback } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { formatAccuracy, getAccuracyDescription } from '@/hooks/useGeolocation';

export const LocationPanel = memo(function LocationPanel() {
  const {
    userLocationState,
    locationPermission,
    locationError,
    isTrackingLocation,
    showLocationPanel,
    setShowLocationPanel,
    goToUserLocation,
    setIsTrackingLocation,
    setUserLocationState,
    setLocationError,
  } = useMapStore();

  // We use the store state directly instead of creating another useGeolocation hook
  // The main geolocation tracking is handled by the hook in MapContainer
  const isTracking = isTrackingLocation;
  const error = locationError;

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    setIsTrackingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocationState({
          coordinates: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude ?? undefined,
          altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
          heading: position.coords.heading ?? undefined,
          speed: position.coords.speed ?? undefined,
          timestamp: position.timestamp,
          source: position.coords.accuracy < 100 ? 'gps' : 'network',
        });
        setLocationError(null);
      },
      (err) => {
        setLocationError(err.message);
        setIsTrackingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, [setIsTrackingLocation, setUserLocationState, setLocationError]);

  const stopTracking = useCallback(() => {
    setIsTrackingLocation(false);
  }, [setIsTrackingLocation]);

  const requestLocation = useCallback(() => {
    startTracking();
  }, [startTracking]);

  const handleClose = useCallback(() => {
    setShowLocationPanel(false);
  }, [setShowLocationPanel]);

  const handleGoToLocation = useCallback(() => {
    goToUserLocation();
  }, [goToUserLocation]);

  const handleRefresh = useCallback(() => {
    requestLocation();
  }, [requestLocation]);

  const handleToggleTracking = useCallback(() => {
    if (isTracking) {
      stopTracking();
    } else {
      startTracking();
    }
  }, [isTracking, startTracking, stopTracking]);

  if (!showLocationPanel) return null;

  const accuracy = userLocationState?.accuracy ?? 0;
  const accuracyDesc = getAccuracyDescription(accuracy);

  return (
    <div className="absolute bottom-24 left-4 right-4 z-panel w-80 max-w-[calc(100vw-2rem)] sm:right-auto bg-white rounded-lg shadow-card animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <LocationIcon className="w-5 h-5 text-primary-600" />
          <h3 className="font-medium text-gray-900">Your Location</h3>
        </div>
        <button
          onClick={handleClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Permission Status */}
        {locationPermission === 'denied' && (
          <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
            <WarningIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Location access blocked</p>
              <p className="text-xs text-red-600 mt-1">
                Your browser has blocked location access. To reset:
              </p>
              <ul className="text-xs text-red-600 mt-2 space-y-1 list-disc list-inside">
                <li><strong>Chrome:</strong> Click the lock icon in the URL bar → Site settings → Location → Allow</li>
                <li><strong>Firefox:</strong> Click the lock icon → Clear permission → Refresh</li>
                <li><strong>Safari:</strong> Settings → Privacy → Location Services → Safari</li>
                <li><strong>Edge:</strong> Click lock icon → Site permissions → Location → Allow</li>
              </ul>
              <p className="text-xs text-red-600 mt-2">
                After resetting, refresh the page and try again.
              </p>
              <button
                onClick={startTracking}
                className="text-xs font-medium text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-md transition-colors mt-3"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && locationPermission !== 'denied' && (
          <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
            <WarningIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800">{error}</p>
            </div>
          </div>
        )}

        {/* Location Data */}
        {userLocationState && (
          <>
            {/* Coordinates */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Coordinates
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Lat:</span>
                  <span className="text-gray-900">{userLocationState.coordinates.lat.toFixed(6)}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-500">Lng:</span>
                  <span className="text-gray-900">{userLocationState.coordinates.lng.toFixed(6)}</span>
                </div>
              </div>
            </div>

            {/* Accuracy */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Accuracy
              </h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AccuracyIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{formatAccuracy(accuracy)}</span>
                </div>
                <span className={`text-sm font-medium ${accuracyDesc.color}`}>
                  {accuracyDesc.label}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${getAccuracyBarColor(accuracy)}`}
                  style={{ width: `${getAccuracyPercentage(accuracy)}%` }}
                />
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 gap-3">
              {/* Altitude */}
              {userLocationState.altitude !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Altitude</div>
                  <div className="text-sm font-medium text-gray-900">
                    {Math.round(userLocationState.altitude)} m
                  </div>
                </div>
              )}

              {/* Speed */}
              {userLocationState.speed !== undefined && userLocationState.speed > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Speed</div>
                  <div className="text-sm font-medium text-gray-900">
                    {(userLocationState.speed * 3.6).toFixed(1)} km/h
                  </div>
                </div>
              )}

              {/* Heading */}
              {userLocationState.heading !== undefined && userLocationState.speed && userLocationState.speed > 0.5 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Heading</div>
                  <div className="text-sm font-medium text-gray-900">
                    {Math.round(userLocationState.heading)}° {getHeadingDirection(userLocationState.heading)}
                  </div>
                </div>
              )}

              {/* Source */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Source</div>
                <div className="text-sm font-medium text-gray-900 capitalize">
                  {userLocationState.source === 'gps' ? 'GPS' : userLocationState.source}
                </div>
              </div>
            </div>

            {/* Last Updated */}
            <div className="text-xs text-gray-500 text-center">
              Last updated: {new Date(userLocationState.timestamp).toLocaleTimeString()}
            </div>
          </>
        )}

        {/* No Location */}
        {!userLocationState && !error && (
          <div className="text-center py-4">
            <LocationIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-600">
              {isTracking ? 'Acquiring location...' : 'Location not available'}
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 p-4 border-t border-gray-100">
        <button
          onClick={handleToggleTracking}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isTracking
              ? 'bg-red-50 text-red-600 hover:bg-red-100'
              : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
          }`}
        >
          {isTracking ? (
            <>
              <StopIcon className="w-4 h-4" />
              Stop Tracking
            </>
          ) : (
            <>
              <PlayIcon className="w-4 h-4" />
              Start Tracking
            </>
          )}
        </button>

        {userLocationState && (
          <>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh location"
            >
              <RefreshIcon className="w-5 h-5" />
            </button>

            <button
              onClick={handleGoToLocation}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go to my location"
            >
              <TargetIcon className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
});

// Helper functions
function getAccuracyPercentage(meters: number): number {
  if (meters < 10) return 100;
  if (meters < 30) return 80;
  if (meters < 100) return 60;
  if (meters < 500) return 40;
  if (meters < 1000) return 20;
  return 10;
}

function getAccuracyBarColor(meters: number): string {
  if (meters < 30) return 'bg-green-500';
  if (meters < 100) return 'bg-yellow-500';
  if (meters < 500) return 'bg-orange-500';
  return 'bg-red-500';
}

function getHeadingDirection(heading: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(heading / 45) % 8;
  return directions[index];
}

// Icons
function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function AccuracyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <circle cx="12" cy="12" r="10" strokeWidth={2} />
      <circle cx="12" cy="12" r="6" strokeWidth={2} />
      <circle cx="12" cy="12" r="2" strokeWidth={2} />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function TargetIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <circle cx="12" cy="12" r="10" strokeWidth={2} />
      <circle cx="12" cy="12" r="3" strokeWidth={2} />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v4m0 12v4M2 12h4m12 0h4" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function StopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <rect x="6" y="6" width="12" height="12" />
    </svg>
  );
}
